import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRanking } from "@/context/RankingContext";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { COLORS } from "@/lib/colors";

interface University {
  rank: number;
  name: string;
  score: number;
}

interface RankingTableProps {
  universities: University[];
  isSimulationMode: boolean;
  hasScoreChanged: boolean;
}

export const RankingTable: React.FC<RankingTableProps> = ({ 
  universities, 
  isSimulationMode,
  hasScoreChanged
}) => {
  const { depaulRank } = useRanking();
  const DEPAUL_ORIGINAL_RANK = 165;
  const DEPAUL_ORIGINAL_SCORE = 55.0;

  return (
    <div className="w-full mt-8">
      <Table>
        <TableHeader>
          <TableRow className="border-b-2 border-[#4FAADD]/20">
            <TableHead className="w-[100px] text-base font-semibold text-[#274791]">Rank</TableHead>
            <TableHead className="text-base font-semibold text-[#274791]">University</TableHead>
            <TableHead className="text-right text-base font-semibold text-[#274791]">Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {universities.map((uni) => {
            const isDepaul = uni.name === "DePaul University";
            const rankChange = isDepaul ? DEPAUL_ORIGINAL_RANK - depaulRank : 0;
            const scoreChange = isDepaul ? uni.score - DEPAUL_ORIGINAL_SCORE : 0;
            
            return (
              <motion.tr
                key={uni.name}
                initial={isDepaul ? { scale: 1 } : {}}
                animate={isDepaul && hasScoreChanged ? { scale: [1, 1.02, 1] } : {}}
                transition={{ duration: 0.3 }}
                className={cn(
                  "group transition-colors duration-200",
                  isDepaul 
                    ? "bg-[#274791]/5 border-y border-[#274791]/20 hover:bg-[#274791]/10" 
                    : "hover:bg-gray-50/80"
                )}
              >
                <TableCell className="font-medium py-4">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-lg font-bold",
                      isDepaul ? "text-[#274791]" : "text-gray-700"
                    )}>
                      #{isDepaul ? depaulRank : uni.rank}
                    </span>
                    {isDepaul && isSimulationMode && hasScoreChanged && rankChange !== 0 && (
                      <motion.span 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={cn(
                          "text-sm font-bold px-2 py-1 rounded-full",
                          rankChange > 0 
                            ? "text-[#84CBBF] bg-[#84CBBF]/10" 
                            : "text-[#D62B2A] bg-[#D62B2A]/10"
                        )}
                      >
                        {rankChange > 0 ? '↑' : '↓'} {Math.abs(rankChange)}
                      </motion.span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className={cn(
                    "text-lg transition-colors duration-200",
                    isDepaul 
                      ? "font-bold text-[#274791]" 
                      : "text-gray-700 group-hover:text-gray-900"
                  )}>
                    {uni.name}
                  </span>
                  {isDepaul && (
                    <span className="ml-2 text-sm font-medium text-[#E36D34]">
                      (Current)
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className={cn(
                      "text-lg font-bold",
                      isDepaul ? "text-[#274791]" : "text-gray-700"
                    )}>
                      {uni.score.toFixed(1)}
                    </span>
                    {isDepaul && isSimulationMode && hasScoreChanged && (
                      <motion.span 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={cn(
                          "text-sm font-bold px-2 py-1 rounded-full",
                          scoreChange > 0 
                            ? "text-[#84CBBF] bg-[#84CBBF]/10" 
                            : "text-[#D62B2A] bg-[#D62B2A]/10"
                        )}
                      >
                        {scoreChange > 0 ? '+' : ''}{scoreChange.toFixed(1)}
                      </motion.span>
                    )}
                  </div>
                </TableCell>
              </motion.tr>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};